import useSWR, { mutate } from 'swr';
import { doc, getDoc as _getDoc, onSnapshot, setDoc, updateDoc } from '@firebase/firestore';
import { fuego } from '../context/Provider';
import { useRef, useEffect, useCallback } from 'react';
import { empty } from '../helpers/empty';
import { collectionCache } from '../classes/Cache';
import { isDev } from '../helpers/is-dev';
import { withDocumentDatesParsed } from '../helpers/doc-date-parser';
import { deleteDoc, shouldMerge } from './static-mutations';
export const getDoc = async function (path) {
  let {
    parseDates,
    ignoreFirestoreDocumentSnapshotField = true
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : empty.object;
  const data = await _getDoc(doc(fuego.db, path)).then(doc => {
    const docData = doc.data({
      serverTimestamps: 'estimate'
    });

    if (isDev && docData && (docData.exists || docData.id || docData.hasPendingWrites)) {
      console.warn('[get-doc] warning: Your document, ', doc.id, ' is using one of the following reserved fields: [exists, id, hasPendingWrites]. These fields are reserved. Please remove them from your documents.');
    }

    return withDocumentDatesParsed({ ...docData,
      id: doc.id,
      exists: doc.exists(),
      hasPendingWrites: doc.metadata.hasPendingWrites,
      __snapshot: ignoreFirestoreDocumentSnapshotField ? undefined : doc
    }, parseDates);
  }); // update the document in any collections listening to the same document

  let collection = path.split(`/${data.id}`);
  collection.pop(); // remove last item, which is the /id

  collection = collection.join('/'); // rejoin the path

  if (collection) {
    collectionCache.getSWRKeysFromCollectionPath(collection).forEach(key => {
      mutate(key, function () {
        let currentState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.array;

        // don't mutate the current state if it doesn't include this doc
        if (!currentState.some(doc => doc.id === data.id)) {
          return currentState;
        }

        return currentState.map(document => {
          if (document.id === data.id) {
            return data;
          }

          return document;
        });
      }, false);
    });
  }

  return data;
};

const createListenerAsync = async function (path) {
  let {
    parseDates,
    ignoreFirestoreDocumentSnapshotField = true
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return await new Promise(resolve => {
    const unsubscribe = onSnapshot(doc(fuego.db, path), doc => {
      const docData = doc.data();
      const data = withDocumentDatesParsed({ ...docData,
        id: doc.id,
        exists: doc.exists(),
        hasPendingWrites: doc.metadata.hasPendingWrites,
        __snapshot: ignoreFirestoreDocumentSnapshotField ? undefined : doc
      }, parseDates);
      mutate(path, data, false);

      if (isDev && docData && (docData.exists || docData.id || docData.hasPendingWrites)) {
        console.warn('[use-document] warning: Your document, ', doc.id, ' is using one of the following reserved fields: [exists, id, hasPendingWrites]. These fields are reserved. Please remove them from your documents.');
      } // update the document in any collections listening to the same document


      let collection = path.split(`/${doc.id}`).filter(Boolean);
      collection.pop(); // remove last item, which is the /id

      collection = collection.join('/');

      if (collection) {
        collectionCache.getSWRKeysFromCollectionPath(collection).forEach(key => {
          mutate(key, function () {
            let currentState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.array;

            // don't mutate the current state if it doesn't include this doc
            if (!currentState.some(doc => doc.id && doc.id === data.id)) {
              return currentState;
            }

            return currentState.map(document => {
              if (document.id === data.id) {
                return data;
              }

              return document;
            });
          }, false);
        });
      } // the first time the listener fires, we resolve the promise with initial data


      resolve({
        initialData: data,
        unsubscribe
      });
    });
  });
};

export const useDocument = function (path) {
  let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : empty.object;
  const unsubscribeRef = useRef(null);
  const {
    listen = false,
    parseDates,
    ignoreFirestoreDocumentSnapshotField = true,
    ...opts
  } = options; // if we're listening, the firestore listener handles all revalidation

  const {
    refreshInterval = listen ? 0 : undefined,
    refreshWhenHidden = listen ? false : undefined,
    refreshWhenOffline = listen ? false : undefined,
    revalidateOnFocus = listen ? false : undefined,
    revalidateOnReconnect = listen ? false : undefined,
    dedupingInterval = listen ? 0 : undefined
  } = options;
  const swrOptions = { ...opts,
    refreshInterval,
    refreshWhenHidden,
    refreshWhenOffline,
    revalidateOnFocus,
    revalidateOnReconnect,
    dedupingInterval
  }; // we move listen to a Ref
  // why? because we shouldn't have to include "listen" in the key
  // if we do, then calling mutate() won't be consistent for all
  // documents with the same path.

  const shouldListen = useRef(listen);
  useEffect(() => {
    shouldListen.current = listen;
  }, [listen]);
  const datesToParse = useRef(parseDates);
  useEffect(() => {
    datesToParse.current = parseDates;
  }, [parseDates]);
  const shouldIgnoreSnapshot = useRef(ignoreFirestoreDocumentSnapshotField);
  useEffect(() => {
    shouldIgnoreSnapshot.current = ignoreFirestoreDocumentSnapshotField;
  }, [ignoreFirestoreDocumentSnapshotField]);
  const swr = useSWR(path, async path => {
    if (shouldListen.current) {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      const {
        unsubscribe,
        initialData
      } = await createListenerAsync(path, {
        parseDates: datesToParse.current,
        ignoreFirestoreDocumentSnapshotField: shouldIgnoreSnapshot.current
      });
      unsubscribeRef.current = unsubscribe;
      return initialData;
    }

    const data = await getDoc(path, {
      parseDates: datesToParse.current,
      ignoreFirestoreDocumentSnapshotField: shouldIgnoreSnapshot.current
    });
    return data;
  }, swrOptions);
  const {
    data,
    isValidating,
    mutate: connectedMutate,
    error
  } = swr; // if listen changes,
  // we run revalidate.
  // This triggers SWR to fetch again
  // Why? because we don't want to put listen or memoQueryString
  // in the useSWR key. If we did, then we couldn't mutate
  // based on path. If we had useSWR(['users', { where: ['name', '==, 'fernando']}]),
  // and we updated the proper `user` dictionary, it wouldn't mutate, because of
  // the key.
  // thus, we move the `listen` and `queryString` options to refs passed to `useSWR`,
  // and we call `revalidate` if either of them change.

  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) revalidateRef.current();else mounted.current = true;
  }, [listen]); // this MUST be after the previous effect to avoid duplicate initial validations.
  // only happens on updates, not initial mount.

  const revalidateRef = useRef(swr.mutate);
  useEffect(() => {
    revalidateRef.current = swr.mutate;
  });
  useEffect(() => {
    return () => {
      // clean up listener on unmount if it exists
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    }; // should depend on the path, and listen being the same...
  }, [path, listen]);
  /**
   * `set(data, SetOptions?)`: Extends the `firestore` document `set` function.
   * - You can call this when you want to edit your document.
   * - It also updates the local cache using SWR's `mutate`. This will prove highly convenient over the regular Firestore `set` function.
   * - The second argument is the same as the second argument for [Firestore `set`](https://firebase.google.com/docs/firestore/manage-data/add-data#set_a_document).
   */

  const set = useCallback(function (data) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    if (!listen) {
      // we only update the local cache if we don't have a listener set up
      // Why? firestore handles this for us for listeners.
      // @ts-ignore
      connectedMutate(prevState => {
        // default we set merge to be false. this is annoying, but follows Firestore's preference.
        if (!shouldMerge(options)) return data;
        return { ...prevState,
          ...data
        };
      });
    }

    if (!path) return null;
    return setDoc(doc(fuego.db, path), data, options);
  }, [path, listen, connectedMutate]);
  /**
   * - `update(data)`: Extends the Firestore document [`update` function](https://firebase.google.com/docs/firestore/manage-data/add-data#update-data).
   * - It also updates the local cache using SWR's `mutate`. This will prove highly convenient over the regular `set` function.
   */

  const update = useCallback(data => {
    if (!listen) {
      // we only update the local cache if we don't have a listener set up
      // @ts-ignore
      connectedMutate(function () {
        let prevState = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : empty.object;
        return { ...prevState,
          ...data
        };
      });
    }

    if (!path) return null;
    return updateDoc(doc(fuego.db, path), data);
  }, [listen, path, connectedMutate]);
  const connectedDelete = useCallback(() => {
    return deleteDoc(path, listen);
  }, [path, listen]);
  return {
    data,
    isValidating,
    mutate: connectedMutate,
    error,
    set,
    update,
    loading: !data && !error,
    deleteDocument: connectedDelete,

    /**
     * A function that, when called, unsubscribes the Firestore listener.
     *
     * The function can be null, so make sure to check that it exists before calling it.
     *
     * **Note**: This is not necessary to use. `useDocument` already unmounts the listener for you. This is only intended if you want to unsubscribe on your own.
     */
    unsubscribe: unsubscribeRef.current
  };
};
//# sourceMappingURL=use-swr-document.js.map